/**
 * Smart Schedule dialog - the one-button bulk-scheduling entry point.
 *
 * Calm defaults, progressive disclosure: scope (selected contractors or
 * every behind-schedule contractor), optional geographic scope (state,
 * service area, city and country chips, each fed by its own candidates-by
 * facet count and hidden when the pool has no values for that axis),
 * trip-size chips, time-per-stop, working
 * day, optional start point; the pool eligibility extras live under an
 * Options fold. Generation produces UNDATED draft itineraries - the
 * result view lists them with skipped/leftover counts surfaced.
 *
 * Form state is owned by the canonical `useForm` helper from
 * @/shared/lib/forms (see CLAUDE.md - Hand-Rolled Forms Are Banned). The
 * bespoke chip groups and the collapsible Options panel ride the `custom`
 * field type; the plain selects/inputs are standard generated fields.
 * Values with no visible control of their own (the free-text custom size,
 * the eligibility toggles) are declared as hidden fields so they keep a
 * default and flow through the submit payload.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ChevronDown, ChevronRight, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import {
  useGenerateItineraries,
  useInspectionSubTypes,
  type ContractorSummary,
  type GenerateItinerariesResult,
} from '@/admin/hooks/use-inspections'

const TRIP_SIZES = [20, 40, 60, 80, 100]
// Trip length was two fixed picks (1 and 2 weeks) while trip size already had
// a Custom free input, so a 3-week trip was only reachable by working out the
// stop count by hand. Longer trips still assign to a single inspector, so the
// answer to "several crews in one region" is several trips from one run, not
// one very long trip - these picks just remove the arithmetic.
const TRIP_WEEKS = [1, 2, 3, 4]
const STOP_MINUTES = [45, 60, 75, 90, 105]
const DAY_HOURS = [6, 8, 10]
const DAY_STARTS = ['07:00', '07:30', '08:00', '08:30', '09:00', '09:30']

/**
 * One scope chip: the value posted to the backend, plus what the chip says.
 * Zones filter on a GeoZone code but read as a place name, so the two are
 * kept separate rather than assuming the label is the value.
 */
type ScopeChip = { value: string; label: string; count: number }

type SmartScheduleFormValues = {
  states: string[]
  cities: string[]
  countries: string[]
  zones: string[]
  trip_size: number
  custom_size: string
  stop_minutes: string
  day_start: string
  day_hours: string
  origin: string
  end_point: string
  cycle_months: string
  paid_only: boolean
  hide_open_claims: boolean
}

// "1 week" = 5 working days x how many stops fit a day. The day estimate
// mirrors what the route optimizer typically lands on: stop time plus a
// ~30 min drive allowance per leg.
function estimateStopsPerDay(dayHours: string, stopMinutes: string): number {
  return Math.max(
    1,
    Math.floor(
      ((Number(dayHours) || 8) * 60) / ((Number(stopMinutes) || 60) + 30),
    ),
  )
}

const chipClassName = (active: boolean) =>
  active
    ? 'rounded-full bg-[var(--green,#1c5a48)] px-3 text-white hover:bg-[var(--green,#1c5a48)]/90'
    : 'rounded-full border-[var(--rule-strong)] px-3 text-[var(--ink-2)] hover:border-[var(--ink-3)]'

export function SmartScheduleDialog({
  open,
  onOpenChange,
  subTypeKey,
  selectedContractorUuids,
  summary,
  initialState = '',
  initialCity = '',
  initialCountry = '',
  initialZone = '',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  subTypeKey: string
  /** Empty = every behind-schedule contractor. */
  selectedContractorUuids: string[]
  summary: ContractorSummary | undefined
  /** Pre-tick one chip per axis (the Pool toolbar's own filters). */
  initialState?: string
  initialCity?: string
  initialCountry?: string
  initialZone?: string
}) {
  const { t } = useTranslation()
  const generate = useGenerateItineraries()
  const [result, setResult] = useState<GenerateItinerariesResult | null>(null)
  // Collapsible Options panel is pure presentation, not form data.
  const [optionsOpen, setOptionsOpen] = useState(false)

  // Which inspection types this run covers, beyond the one the Pool toolbar
  // has selected. Amanda asked for the types to be individually selectable
  // "at will" - when a trip cannot fit everyone, a new qualifying gets picked
  // over an ordinary pool stop, so type is a selection criterion, not just a
  // filter. Nothing is pre-ticked: "at will" implies neither state is assumed.
  const [extraSubTypeKeys, setExtraSubTypeKeys] = useState<string[]>([])
  const [includePriority, setIncludePriority] = useState(false)
  const { data: allSubTypes } = useInspectionSubTypes()
  const extraSubTypeOptions = useMemo(
    () =>
      (allSubTypes ?? []).filter(
        (st) => st.requires_holding && st.key !== subTypeKey,
      ),
    [allSubTypes, subTypeKey],
  )

  // The scope facets. Each list is whatever the pool actually contains, so
  // the coordinator can only ever pick a value that exists - which is what
  // keeps city affordable: no free text, so nothing to normalise.
  const stateOptions = useMemo<ScopeChip[]>(
    () =>
      (summary?.candidates_by_state ?? [])
        .filter((s) => s.state)
        .map((s) => ({ value: s.state, label: s.state, count: s.count })),
    [summary],
  )
  const cityOptions = useMemo<ScopeChip[]>(
    () =>
      (summary?.candidates_by_city ?? [])
        .filter((c) => c.city)
        .map((c) => ({ value: c.city, label: c.city, count: c.count })),
    [summary],
  )
  const countryOptions = useMemo<ScopeChip[]>(
    () =>
      (summary?.candidates_by_country ?? [])
        .filter((c) => c.country)
        .map((c) => ({ value: c.country, label: c.country, count: c.count })),
    [summary],
  )
  const zoneOptions = useMemo<ScopeChip[]>(
    () =>
      (summary?.candidates_by_zone ?? [])
        .filter((z) => z.zone)
        .map((z) => ({
          value: z.zone,
          label: z.label || z.zone,
          count: z.count,
        })),
    [summary],
  )

  const fields = useMemo<FormField[]>(() => {
    // One chip-group builder for all four axes - they differ only in their
    // label, their option list and which form value they write.
    const scopeGroup = (
      name: 'states' | 'cities' | 'countries' | 'zones',
      label: string,
      options: ScopeChip[],
      initial: string,
    ): FormField => ({
      type: 'custom',
      name,
      className: 'col-span-6',
      // Only pre-tick a value the facet list actually offers, so a stale
      // toolbar filter cannot scope a run to something with no work in it.
      value:
        initial && options.some((o) => o.value === initial) ? [initial] : [],
      visible: options.length > 0,
      validationSchema: z.array(z.string()),
      render: (ctx) => {
        const selected = (ctx.values[name] as string[]) ?? []
        const toggle = (value: string) =>
          ctx.setFieldValue(
            name,
            selected.includes(value)
              ? selected.filter((s) => s !== value)
              : [...selected, value],
          )
        return (
          <div className="flex flex-col gap-1.5">
            <Label>
              {label}{' '}
              <span className="font-normal text-[var(--ink-3)]">
                {t(
                  'inspections.smartSchedule.statesOptionalHint',
                  '(optional - all when none picked)',
                )}
              </span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {options.map((o) => (
                <Button
                  key={o.value}
                  type="button"
                  size="xs"
                  variant={selected.includes(o.value) ? 'default' : 'outline'}
                  className={chipClassName(selected.includes(o.value))}
                  onClick={() => toggle(o.value)}
                >
                  {o.label} · {o.count}
                </Button>
              ))}
            </div>
          </div>
        )
      },
    })

    const list: FormField[] = [
      scopeGroup(
        'states',
        t('inspections.smartSchedule.statesLabel', 'States'),
        stateOptions,
        initialState,
      ),
      // Zones (plant service areas) sit next to states because they are the
      // unit a trip is actually built and labelled by.
      scopeGroup(
        'zones',
        t('inspections.smartSchedule.serviceAreasLabel', 'Service areas'),
        zoneOptions,
        initialZone,
      ),
      scopeGroup(
        'cities',
        t('inspections.smartSchedule.citiesLabel', 'Cities'),
        cityOptions,
        initialCity,
      ),
      scopeGroup(
        'countries',
        t('inspections.smartSchedule.countriesLabel', 'Countries'),
        countryOptions,
        initialCountry,
      ),
      // Day shape first - the trip-size quick-picks below read stops/day
      // from these three settings, so they belong above it.
      {
        type: 'select',
        name: 'stop_minutes',
        label: t('inspections.smartSchedule.timePerStop', 'Time per stop'),
        value: '60',
        className: 'col-span-2',
        options: STOP_MINUTES.map((m) => ({
          value: String(m),
          label:
            m >= 60
              ? m % 60
                ? t(
                    'inspections.smartSchedule.durationHoursMinutes',
                    '{{hours}} h {{minutes}} m',
                    { hours: Math.floor(m / 60), minutes: m % 60 },
                  )
                : t('inspections.smartSchedule.durationHours', '{{hours}} h', {
                    hours: Math.floor(m / 60),
                  })
              : t('inspections.smartSchedule.durationMinutes', '{{minutes}} m', {
                  minutes: m,
                }),
        })),
      },
      {
        type: 'select',
        name: 'day_start',
        label: t('inspections.smartSchedule.dayStartLabel', 'Day start'),
        value: '09:00',
        className: 'col-span-2',
        options: DAY_STARTS.map((dt) => ({ value: dt, label: dt })),
      },
      {
        type: 'select',
        name: 'day_hours',
        label: t('inspections.smartSchedule.workingDayLabel', 'Working day'),
        value: '8',
        className: 'col-span-2',
        options: DAY_HOURS.map((h) => ({
          value: String(h),
          label: t('inspections.smartSchedule.durationHours', '{{hours}} h', {
            hours: h,
          }),
        })),
      },
      // One trip-size control: pick an exact stop count (chips or Custom),
      // or use the week quick-picks, which size stops from the day settings
      // above. Both write `trip_size`, so there is a single source of truth.
      {
        type: 'custom',
        name: 'trip_size',
        className: 'col-span-6',
        value: 40,
        validationSchema: z.number(),
        render: (ctx) => {
          const tripSize = Number(ctx.values.trip_size) || 0
          const customSize = (ctx.values.custom_size as string) ?? ''
          const perDay = estimateStopsPerDay(
            (ctx.values.day_hours as string) ?? '8',
            (ctx.values.stop_minutes as string) ?? '60',
          )
          return (
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <Label>
                  {t('inspections.smartSchedule.tripSizeLabel', 'Trip size')}{' '}
                  <span className="font-normal text-[var(--ink-3)]">
                    {t(
                      'inspections.smartSchedule.tripSizeHint',
                      '(stops per trip)',
                    )}
                  </span>
                </Label>
                <span className="text-[11px] text-[var(--ink-3)]">
                  {t(
                    'inspections.smartSchedule.stopsPerDayHint',
                    '~{{count}} stops/day',
                    { count: perDay },
                  )}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {TRIP_SIZES.map((n) => {
                  const active = !customSize && tripSize === n
                  return (
                    <Button
                      key={n}
                      type="button"
                      size="xs"
                      variant={active ? 'default' : 'outline'}
                      className={chipClassName(active)}
                      onClick={() => {
                        ctx.setFieldValue('trip_size', n)
                        ctx.setFieldValue('custom_size', '')
                      }}
                    >
                      {n}
                    </Button>
                  )
                })}
                <Input
                  type="number"
                  min={5}
                  max={200}
                  step={1}
                  placeholder={t(
                    'inspections.smartSchedule.customSizePlaceholder',
                    'Custom',
                  )}
                  value={customSize}
                  onChange={(e) =>
                    ctx.setFieldValue('custom_size', e.target.value)
                  }
                  className="h-8 w-20 text-xs"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-[var(--ink-3)]">
                  {t(
                    'inspections.smartSchedule.quickPickByWeeks',
                    'Quick-pick by weeks:',
                  )}
                </span>
                {TRIP_WEEKS.map((weeks) => {
                  const capacity = weeks * 5 * perDay
                  const active = !customSize && tripSize === capacity
                  return (
                    <Button
                      key={weeks}
                      type="button"
                      size="xs"
                      variant={active ? 'default' : 'outline'}
                      className={chipClassName(active)}
                      onClick={() => {
                        ctx.setFieldValue('trip_size', capacity)
                        ctx.setFieldValue('custom_size', '')
                      }}
                    >
                      {t(
                        'inspections.smartSchedule.tripLengthWeeks',
                        '{{count}} weeks',
                        { count: weeks },
                      )}{' '}
                      · ~{capacity}
                    </Button>
                  )
                })}
              </div>
            </div>
          )
        },
      },
      // Labels kept short (no "(optional)" parenthetical) so the uppercase
      // generated-form labels stay on one line in the half-width columns and
      // the row reads symmetrically; the hint below covers that both are
      // optional.
      {
        type: 'text',
        name: 'origin',
        label: t('inspections.smartSchedule.startPointLabel', 'Start point'),
        value: '',
        autoCapitalize: false,
        placeholder: t(
          'inspections.smartSchedule.startPointPlaceholder',
          'John Glenn Columbus Intl Airport',
        ),
        className: 'col-span-3',
      },
      {
        type: 'text',
        name: 'end_point',
        label: t('inspections.smartSchedule.endPointLabel', 'End point'),
        value: '',
        autoCapitalize: false,
        placeholder: t(
          'inspections.smartSchedule.endPointPlaceholder',
          'Same airport, hotel…',
        ),
        className: 'col-span-3',
        hint: t(
          'inspections.smartSchedule.endPointHint',
          'Optional. Resolved to a real place on the map when the trips are drafted - full names work best.',
        ),
      },
      {
        type: 'custom',
        name: 'options',
        className: 'col-span-6',
        value: '',
        validationSchema: z.any(),
        render: (ctx) => {
          const cycleMonths = (ctx.values.cycle_months as string) ?? ''
          const paidOnly = Boolean(ctx.values.paid_only)
          const hideOpenClaims = Boolean(ctx.values.hide_open_claims)
          return (
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setOptionsOpen((v) => !v)}
                className="self-start px-0 text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
              >
                {optionsOpen ? (
                  <ChevronDown size={13} />
                ) : (
                  <ChevronRight size={13} />
                )}
                {t(
                  'inspections.smartSchedule.optionsToggle',
                  'Options - cycle window, eligibility',
                )}
              </Button>
              {optionsOpen && (
                <div className="flex flex-col gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">
                      {t(
                        'inspections.smartSchedule.reinspectionCycleLabel',
                        'Re-inspection cycle',
                      )}
                    </Label>
                    <Select
                      value={cycleMonths || 'none'}
                      onValueChange={(v: string) =>
                        ctx.setFieldValue(
                          'cycle_months',
                          v === 'none' ? '' : v,
                        )
                      }
                    >
                      <SelectTrigger className="h-8 w-[180px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {t(
                            'inspections.smartSchedule.noCycleWindow',
                            'No cycle window',
                          )}
                        </SelectItem>
                        {[12, 18, 24, 36].map((m) => (
                          <SelectItem key={m} value={String(m)}>
                            {t(
                              'inspections.smartSchedule.dueAfterMonths',
                              'Due after {{months}} months',
                              { months: m },
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Label className="cursor-pointer text-xs font-normal text-[var(--ink-2)]">
                    <Checkbox
                      checked={paidOnly}
                      onCheckedChange={(v) =>
                        ctx.setFieldValue('paid_only', v === true)
                      }
                    />
                    {t(
                      'inspections.smartSchedule.paidOnlyLabel',
                      'Paid warranties only (the bulk programme)',
                    )}
                  </Label>
                  <Label className="cursor-pointer text-xs font-normal text-[var(--ink-2)]">
                    <Checkbox
                      checked={hideOpenClaims}
                      onCheckedChange={(v) =>
                        ctx.setFieldValue('hide_open_claims', v === true)
                      }
                    />
                    {t(
                      'inspections.smartSchedule.hideOpenClaimsLabel',
                      'Skip homes with open claims',
                    )}
                  </Label>

                  {extraSubTypeOptions.length > 0 && (
                    <div className="flex flex-col gap-1.5 border-t-[0.5px] border-t-[var(--rule)] pt-3">
                      <span className="text-[11px] text-[var(--ink-3)]">
                        {t(
                          'inspections.smartSchedule.alsoIncludeTypes',
                          'Also include these inspection types:',
                        )}
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {extraSubTypeOptions.map((st) => {
                          const active = extraSubTypeKeys.includes(st.key)
                          return (
                            <Button
                              key={st.key}
                              type="button"
                              size="xs"
                              variant={active ? 'default' : 'outline'}
                              className={chipClassName(active)}
                              onClick={() =>
                                setExtraSubTypeKeys((prev) =>
                                  prev.includes(st.key)
                                    ? prev.filter((k) => k !== st.key)
                                    : [...prev, st.key],
                                )
                              }
                            >
                              {st.label}
                            </Button>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <Label className="cursor-pointer text-xs font-normal text-[var(--ink-2)]">
                    <Checkbox
                      checked={includePriority}
                      onCheckedChange={(v) => setIncludePriority(v === true)}
                    />
                    {t(
                      'inspections.smartSchedule.includePriorityLabel',
                      'Include priority work (claims, manual and hand-marked)',
                    )}
                  </Label>
                </div>
              )}
            </div>
          )
        },
      },
      // Hidden value holders: their controls live inside the custom fields
      // above, but they need a declared default + registration so they seed
      // sensible values and flow through the submit payload.
      {
        type: 'text',
        name: 'custom_size',
        value: '',
        visible: false,
        validationSchema: z.any(),
      },
      {
        type: 'text',
        name: 'cycle_months',
        value: '',
        visible: false,
        validationSchema: z.any(),
      },
      {
        type: 'checkbox',
        name: 'paid_only',
        label: '',
        value: true,
        visible: false,
        validationSchema: z.any(),
      },
      {
        type: 'checkbox',
        name: 'hide_open_claims',
        label: '',
        value: true,
        visible: false,
        validationSchema: z.any(),
      },
    ]
    return list
    // extraSubTypeKeys / includePriority are read by the Options fold's
    // renderer, so they belong here - without them the chips would not
    // re-render when toggled (the memo would hand back a stale closure).
  }, [
    stateOptions,
    cityOptions,
    countryOptions,
    zoneOptions,
    initialState,
    initialCity,
    initialCountry,
    initialZone,
    optionsOpen,
    extraSubTypeOptions,
    extraSubTypeKeys,
    includePriority,
    t,
  ])

  const { Form, ref: formRef } = useForm(
    {
      name: 'smart-schedule-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as SmartScheduleFormValues
      // Custom trip size is free-text numeric input. An empty string falls
      // back to the selected preset chip (not 0/NaN), and any parsed value
      // is rounded to a whole number of stops before it is ever clamped or
      // submitted - the field has no `step` restriction, so a user can type
      // a decimal like "45.5" and it must not be sent to the backend as-is.
      const parsedCustomSize =
        !values.custom_size || values.custom_size.trim() === ''
          ? null
          : Number(values.custom_size)
      const effectiveSize =
        parsedCustomSize != null && Number.isFinite(parsedCustomSize)
          ? Math.round(parsedCustomSize)
          : Number(values.trip_size) || 40
      try {
        const res = await generate.mutateAsync({
          sub_type_key: subTypeKey,
          sub_type_keys: extraSubTypeKeys.length ? extraSubTypeKeys : undefined,
          include_priority: includePriority || undefined,
          contractor_uuids: selectedContractorUuids.length
            ? selectedContractorUuids
            : undefined,
          states: values.states?.length ? values.states : undefined,
          cities: values.cities?.length ? values.cities : undefined,
          countries: values.countries?.length ? values.countries : undefined,
          zones: values.zones?.length ? values.zones : undefined,
          target_stops: Math.min(Math.max(effectiveSize, 5), 200),
          per_stop_minutes: Number(values.stop_minutes) || 60,
          max_day_minutes: (Number(values.day_hours) || 8) * 60,
          origin_address: values.origin.trim() || undefined,
          end_address: values.end_point.trim() || undefined,
          day_start_time: values.day_start,
          cycle_months: values.cycle_months
            ? Number(values.cycle_months)
            : undefined,
          has_paid_warranty: values.paid_only ? true : null,
          exclude_with_open_claims: values.hide_open_claims,
        })
        setResult(res)
        if (res.summary.itineraries === 0) {
          toast.info(
            t(
              'inspections.smartSchedule.toastNoItineraries',
              'No itineraries generated - nothing routable matched the scope.',
            ),
          )
        }
      } catch (e) {
        toastApiError(
          e,
          t(
            'inspections.smartSchedule.toastGenerateUnavailable',
            'Smart Schedule is unavailable right now.',
          ),
        )
      }
    },
    // `useForm` value-keys its field config by JSON, and JSON omits function
    // properties - so a `type:'custom'` field's `render` closure is invisible
    // to that key. Passing `fields` here does nothing about it: the array
    // serialises identically whether the Options fold is open or shut. Every
    // piece of component state those closures read therefore has to be listed
    // explicitly, or the memo hands back the previous closure and clicking
    // Options silently does nothing. That is not hypothetical - it is exactly
    // what happened whenever the pool had no candidate states, because then
    // nothing else ever changed the field shape to bust the memo for us.
    [
      open,
      subTypeKey,
      initialState,
      initialCity,
      initialCountry,
      initialZone,
      optionsOpen,
      extraSubTypeKeys,
      extraSubTypeOptions,
      includePriority,
      stateOptions,
      cityOptions,
      countryOptions,
      zoneOptions,
    ],
  )

  const close = (next: boolean) => {
    if (!next) {
      setResult(null)
      setOptionsOpen(false)
      // Reset the type/priority selection too, so reopening the dialog does
      // not silently carry the last run's scope into the next one.
      setExtraSubTypeKeys([])
      setIncludePriority(false)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('inspections.smartSchedule.dialogTitle', 'Smart Schedule')}
          </DialogTitle>
          <DialogDescription>
            {selectedContractorUuids.length
              ? t(
                  'inspections.smartSchedule.contractorsSelected',
                  selectedContractorUuids.length === 1
                    ? '{{count}} contractor selected.'
                    : '{{count}} contractors selected.',
                  { count: selectedContractorUuids.length },
                )
              : t(
                  'inspections.smartSchedule.everyContractor',
                  'Every behind-schedule contractor.',
                )}{' '}
            {t(
              'inspections.smartSchedule.descriptionBody',
              'Stages what is due, clusters it into regional trips, and drafts itineraries for review - nothing lands on a calendar yet.',
            )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col gap-3 py-1">
            <p className="text-sm font-medium text-[var(--ink)]">
              {t(
                'inspections.smartSchedule.itinerariesDrafted',
                result.summary.itineraries === 1
                  ? '{{count}} itinerary drafted'
                  : '{{count}} itineraries drafted',
                { count: result.summary.itineraries },
              )}{' '}
              ·{' '}
              {t('inspections.smartSchedule.stopsCount', '{{count}} stops', {
                count: result.summary.stops,
              })}
            </p>
            <div className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
              {result.itineraries.map((it) => (
                <Link
                  key={it.uuid}
                  to={`/inspections/itineraries/${it.uuid}`}
                  className="flex items-center justify-between rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-3 py-2 text-xs hover:border-[var(--ink-3)]"
                >
                  <span className="font-medium text-[var(--ink)]">
                    {it.name}
                  </span>
                  <span className="text-[var(--ink-3)]">
                    {t(
                      'inspections.smartSchedule.stopsCount',
                      '{{count}} stops',
                      { count: it.stop_count },
                    )}{' '}
                    ·{' '}
                    {t(
                      'inspections.smartSchedule.dayCount',
                      it.day_count === 1 ? '{{count}} day' : '{{count}} days',
                      { count: it.day_count },
                    )}
                  </span>
                </Link>
              ))}
            </div>
            <p className="text-[11px] text-[var(--ink-3)]">
              {result.staged_created > 0 &&
                t(
                  'inspections.smartSchedule.newlyStaged',
                  '{{count}} newly staged. ',
                  { count: result.staged_created },
                )}
              {result.leftover_count > 0 &&
                t(
                  'inspections.smartSchedule.leftoverCount',
                  '{{count}} left for the next run (small or capped batches). ',
                  { count: result.leftover_count },
                )}
              {result.skipped.length > 0 &&
                t(
                  'inspections.smartSchedule.skippedCount',
                  '{{count}} skipped (no usable address).',
                  { count: result.skipped.length },
                )}
            </p>
          </div>
        ) : (
          <Form className="grid grid-cols-6 gap-4 py-1" />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            {result
              ? t('inspections.smartSchedule.doneButton', 'Done')
              : t('inspections.smartSchedule.cancelButton', 'Cancel')}
          </Button>
          {!result && (
            <Button
              onClick={() => void formRef.current?.submitForm()}
              disabled={generate.isPending}
              className="bg-[var(--green,#1c5a48)] text-white hover:bg-[var(--green,#1c5a48)]/90"
            >
              {generate.isPending ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  {t('inspections.smartSchedule.routingLoading', 'Routing…')}
                </>
              ) : (
                <>
                  <Sparkles size={14} className="mr-1.5" />
                  {t(
                    'inspections.smartSchedule.generateButton',
                    'Generate itineraries',
                  )}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
